import {assert} from "@prosay/common"
import {getLogger} from "@prosay/logging"
import React, {useCallback, useContext, useMemo, useState} from "react"
import {View} from "react-native"
import ImageViewer from "react-native-image-zoom-viewer"
import {IconButton, Modal, Portal} from "react-native-paper"
import {GetMessagesQuery} from "../graphql"

const logger = getLogger(__filename)

interface ConversationImageInfoMapping {
    [id: string]: number
}
interface ConversationImage {
    show: (id: string) => void
}
const ConversationImageContext = React.createContext<
    ConversationImage | undefined
>(undefined)
export const useConversationImage = logger.debug(() => {
    const value = useContext(ConversationImageContext)
    assert(
        value !== undefined,
        `Was not able to find an active ConversationImageContext instance. Are you sure you have wrapped your view in ConversationImageContextProvider?`,
    )
    return value
}, "useConversationImage")

export interface ConversationImageContextProviderProps {
    data: GetMessagesQuery | undefined
}
export const ConversationImageContextProvider: React.FC<ConversationImageContextProviderProps> = ({
    children,
    data,
}) => {
    const [index, setIndex] = useState<number>()
    const visible = useMemo(() => index !== undefined, [index])
    const list = useMemo(
        () =>
            data?.getMessages
                ?.filter(
                    ({content: {__typename}}) => __typename === "ImageContent",
                )
                ?.map(message => {
                    assert(message.content.__typename === "ImageContent")
                    return {
                        id: message.id,
                        url: message.content.image.presignedUrl,
                    }
                }) ?? [],
        [data],
    )
    const mapping: ConversationImageInfoMapping = useMemo(
        () =>
            Object.fromEntries(list.map(({id}, index) => [id, index] as const)),
        [list],
    )
    const images = useMemo(() => list.map(({url}) => url), [list])
    const show = useCallback((id: string) => setIndex(mapping[id]), [mapping])

    return (
        <>
            <ConversationImageContext.Provider value={{show}}>
                {children}
            </ConversationImageContext.Provider>

            <ImageModalView
                onCancel={() => setIndex(undefined)}
                {...{images, index, visible}}
            />
        </>
    )
}

export interface ImageModalViewProps {
    images: string[]
    index: number | undefined
    onCancel: () => void
    visible: boolean
}
export const ImageModalView: React.FC<ImageModalViewProps> = ({
    images,
    index,
    onCancel,
    visible,
}) => {
    const imageUrls = useMemo(() => images.map(url => ({url})), [images])
    const renderHeader = useCallback(
        () => (
            <View style={{flexDirection: "row"}}>
                <View style={{flex: 1}} />
                <IconButton
                    size={40}
                    icon="close-box"
                    color="white"
                    onPress={onCancel}
                />
            </View>
        ),
        [onCancel],
    )

    return (
        <Portal>
            <Modal
                onDismiss={onCancel}
                contentContainerStyle={{flex: 1}}
                {...{visible}}>
                <ImageViewer
                    enableSwipeDown
                    saveToLocalByLongPress
                    {...{imageUrls, index, onCancel, renderHeader}}
                />
            </Modal>
        </Portal>
    )
}
